/*!
 * Audio/Video tracking for Snowplow v4.0.4 (http://bit.ly/sp-js)
 * Copyright 2022 Snowplow Analytics Ltd
 * Licensed under BSD-3-Clause
 */

import { __assign } from 'tslib';
import { LOG as LOG$1 } from '@snowplow/tracker-core';
import { startMediaTracking, trackMediaReady, trackMediaPlay, trackMediaPause, trackMediaEnd, trackMediaSeekEnd, trackMediaPlaybackRateChange, trackMediaVolumeChange, trackMediaFullscreenChange, trackMediaPictureInPictureChange, trackMediaBufferStart, trackMediaBufferEnd, trackMediaError, updateMediaTracking, SnowplowMediaPlugin, endMediaTracking } from '@snowplow/browser-plugin-media';

var SEARCH_ERROR;
(function (SEARCH_ERROR) {
    SEARCH_ERROR["NOT_FOUND"] = "Media element not found";
    SEARCH_ERROR["MULTIPLE_ELEMENTS"] = "More than one media element in the provided node";
    SEARCH_ERROR["PLYR_CURRENTSRC"] = "Plyr currentSrc not updated";
})(SEARCH_ERROR || (SEARCH_ERROR = {}));
var READY_STATE = {
    0: 'HAVE_NOTHING',
    1: 'HAVE_METADATA',
    2: 'HAVE_CURRENT_DATA',
    3: 'HAVE_FUTURE_DATA',
    4: 'HAVE_ENOUGH_DATA',
};
var NETWORK_STATE = {
    0: 'NETWORK_EMPTY',
    1: 'NETWORK_IDLE',
    2: 'NETWORK_LOADING',
    3: 'NETWORK_NO_SOURCE',
};

function isFullScreen(el) {
    return document.fullscreenElement === el || false;
}
function isHtmlMediaElement(element) {
    return element instanceof HTMLMediaElement;
}
function isHtmlAudioElement(element) {
    return element instanceof HTMLAudioElement;
}
function isHtmlVideoElement(element) {
    return element instanceof HTMLVideoElement;
}
function getUriFileExtension(uri) {
    // greedily match until the final '.' is found with trailing characters, capture those as extension
    // only capture until finding URI metacharacters
    var pattern = /.+\.([^\.?&#]+)/;
    var uriPath = uri;
    try {
        uriPath = new URL(uri).pathname;
    }
    catch (e) { }
    // try to match against the pathname only first, if unsuccessful try against the full URI
    var match = pattern.exec(uriPath) || pattern.exec(uri);
    return match && match[1];
}
function getDuration(el) {
    var duration = el.duration;
    // A NaN value is returned if duration is not available, or Infinity if the media resource is streaming.
    if (isNaN(duration) || duration === Infinity) {
        return null;
    }
    return duration;
}
// Checks if a url is a data_url, so we don't send a (potentially large) payload
function dataUrlHandler(url) {
    if (url.indexOf('data:') !== -1) {
        return 'DATA_URL';
    }
    return url;
}
function timeRangesToObjectArray(t) {
    var out = [];
    for (var i = 0; i < t.length; i++) {
        out.push({ start: t.start(i), end: t.end(i) });
    }
    return out;
}
function textTrackListToJson(textTrackList) {
    return Object.keys(textTrackList).map(function (_, i) {
        return {
            label: textTrackList[i].label,
            language: textTrackList[i].language,
            kind: textTrackList[i].kind,
            mode: textTrackList[i].mode,
        };
    });
}
function setConfigDefaults(config) {
    var defaults = {
        boundaries: [10, 25, 50, 75],
    };
    return __assign(__assign({}, defaults), config);
}
function parseVolume(volume) {
    return parseInt((volume * 100).toString());
}

/**
 * Waits for an HTML media element and invokes a callback with the element once found.
 *
 * - First, it tries to find the media element by id
 *   - If found and valid (`isHtmlMediaElement()`), the callback is called with the updated config.
 *   - If not found, a warning is logged and a `MutationObserver` is set up.
 *
 * - The `MutationObserver` watches for new elements added to the DOM. When the target media element is detected,
 *   the callback is invoked, and the observer disconnects.
 *
 * Useful for cases where the media element might not be in the DOM immediately.
 */
function waitForElement(config, callback) {
    var _a = findMediaElement(config.video), el = _a.el, err = _a.err;
    if (err) {
        LOG$1.info("".concat(err, ". Waiting for element to be added to the DOM."));
    }
    if (isHtmlMediaElement(el)) {
        callback(__assign(__assign({}, config), { video: el }));
    }
    else {
        var observer_1 = new MutationObserver(function (mutations) {
            mutations.forEach(function (mut) {
                if (!mut.addedNodes) {
                    return;
                }
                for (var _i = 0, _a = Object.values(mut.addedNodes); _i < _a.length; _i++) {
                    var node = _a[_i];
                    if (isHtmlMediaElement(node) && node.id === config.video) {
                        callback(__assign(__assign({}, config), { video: node }));
                        observer_1.disconnect();
                    }
                }
            });
        });
        observer_1.observe(document.body, { childList: true, subtree: true });
    }
}
function findMediaElement(id) {
    var el = document.getElementById(id);
    if (!el)
        return { err: SEARCH_ERROR.NOT_FOUND };
    if (isHtmlAudioElement(el))
        return { el: el };
    if (isHtmlVideoElement(el)) {
        // Plyr loads in an initial blank video with currentSrc as https://cdn.plyr.io/static/blank.mp4
        // so we need to check until currentSrc updates.
        if (el.currentSrc === 'https://cdn.plyr.io/static/blank.mp4' && el.readyState === 0) {
            return { err: SEARCH_ERROR.PLYR_CURRENTSRC };
        }
        return { el: el };
    }
    return findMediaElementChild(el);
}
function findMediaElementChild(el) {
    for (var _i = 0, _a = ['VIDEO', 'AUDIO']; _i < _a.length; _i++) {
        var tag = _a[_i];
        var descendentTags = el.getElementsByTagName(tag);
        if (descendentTags.length === 1) {
            var el_1 = descendentTags[0];
            if (isHtmlAudioElement(el_1) || isHtmlVideoElement(el_1)) {
                return { el: el_1 };
            }
        }
        else if (descendentTags.length === 2 && tag === 'VIDEO' && isHtmlVideoElement(descendentTags[0])) {
            // Special JWPlayer case where two video elements are used for cover-video effect.
            // In that case, we select the first video element.
            return { el: descendentTags[0] };
        }
        else if (descendentTags.length > 1) {
            return { err: SEARCH_ERROR.MULTIPLE_ELEMENTS };
        }
    }
    return { err: SEARCH_ERROR.NOT_FOUND };
}

function isStringConfig(config) {
    return typeof config.video === 'string';
}
function isElementConfig(config) {
    return config.video instanceof HTMLMediaElement;
}

/** Type of media player event */
var MediaEventType;
(function (MediaEventType) {
    // Controlling the playback
    /** Media player event fired when the media tracking is successfully attached to the player and can track events. */
    MediaEventType["Ready"] = "ready";
    /** Media player event sent when the player changes state to playing from previously being paused. */
    MediaEventType["Play"] = "play";
    /** Media player event sent when the user pauses the playback. */
    MediaEventType["Pause"] = "pause";
    /** Media player event sent when playback stops when end of the media is reached or because no further data is available. */
    MediaEventType["End"] = "end";
    /** Media player event sent when a seek operation begins. */
    MediaEventType["SeekStart"] = "seek_start";
    /** Media player event sent when a seek operation completes. */
    MediaEventType["SeekEnd"] = "seek_end";
    // Changes in playback settings
    /** Media player event sent when the playback rate has changed. */
    MediaEventType["PlaybackRateChange"] = "playback_rate_change";
    /** Media player event sent when the volume has changed. */
    MediaEventType["VolumeChange"] = "volume_change";
    /** Media player event fired immediately after the browser switches into or out of full-screen mode. */
    MediaEventType["FullscreenChange"] = "fullscreen_change";
    /** Media player event fired immediately after the browser switches into or out of picture-in-picture mode. */
    MediaEventType["PictureInPictureChange"] = "picture_in_picture_change";
    // Tracking playback progress
    /** Media player event fired periodically during main content playback, regardless of other API events that have been sent. */
    MediaEventType["Ping"] = "ping";
    /** Media player event fired when a percentage boundary set in options.boundaries is reached */
    MediaEventType["PercentProgress"] = "percent_progress";
    // Ad events
    /** Media player event that signals the start of an ad break. */
    MediaEventType["AdBreakStart"] = "ad_break_start";
    /** Media player event that signals the end of an ad break. */
    MediaEventType["AdBreakEnd"] = "ad_break_end";
    /** Media player event that signals the start of an ad. */
    MediaEventType["AdStart"] = "ad_start";
    /** Media player event fired when a quartile of ad is reached after continuous ad playback at normal speed. */
    MediaEventType["AdFirstQuartile"] = "ad_first_quartile";
    /** Media player event fired when a midpoint of ad is reached after continuous ad playback at normal speed. */
    MediaEventType["AdMidpoint"] = "ad_midpoint";
    /** Media player event fired when a quartile of ad is reached after continuous ad playback at normal speed. */
    MediaEventType["AdThirdQuartile"] = "ad_third_quartile";
    /** Media player event that signals the ad creative was played to the end at normal speed. */
    MediaEventType["AdComplete"] = "ad_complete";
    /** Media player event fired when the user activated a skip control to skip the ad creative. */
    MediaEventType["AdSkip"] = "ad_skip";
    /** Media player event fired when the user clicked on the ad. */
    MediaEventType["AdClick"] = "ad_click";
    /** Media player event fired when the user clicked the pause control and stopped the ad creative. */
    MediaEventType["AdPause"] = "ad_pause";
    /** Media player event fired when the user resumed playing the ad creative after it had been stopped or paused. */
    MediaEventType["AdResume"] = "ad_resume";
    // Data quality events
    /** Media player event fired when the player goes into the buffering state and begins to buffer content. */
    MediaEventType["BufferStart"] = "buffer_start";
    /** Media player event fired when the the player finishes buffering content and resumes playback. */
    MediaEventType["BufferEnd"] = "buffer_end";
    /** Media player event tracked when the video playback quality changes automatically. */
    MediaEventType["QualityChange"] = "quality_change";
    /** Media player event tracked when the resource could not be loaded due to an error.  */
    MediaEventType["Error"] = "error";
})(MediaEventType || (MediaEventType = {}));
/** Type of media content. */
var MediaType;
(function (MediaType) {
    /** Audio content. */
    MediaType["Audio"] = "audio";
    /** Video content. */
    MediaType["Video"] = "video";
})(MediaType || (MediaType = {}));
/** Type of ads within the break */
var MediaAdBreakType;
(function (MediaAdBreakType) {
    /** Take full control of the video for a period of time. */
    MediaAdBreakType["Linear"] = "linear";
    /** Run concurrently to the video. */
    MediaAdBreakType["NonLinear"] = "nonlinear";
    /** Accompany the video but placed outside the player. */
    MediaAdBreakType["Companion"] = "companion";
})(MediaAdBreakType || (MediaAdBreakType = {}));

function buildHTMLMediaElementEntity(el) {
    var _a, _b;
    var data = {
        // htmlId is a required property in the schema, but may not be present if
        // the user provided the element themselves
        htmlId: el.id || '',
        mediaType: el.tagName,
        autoPlay: el.autoplay,
        buffered: timeRangesToObjectArray(el.buffered),
        controls: el.controls,
        currentSrc: el.currentSrc,
        defaultMuted: el.defaultMuted || false,
        defaultPlaybackRate: el.defaultPlaybackRate,
        error: el.error ? { code: (_a = el.error) === null || _a === void 0 ? void 0 : _a.code, message: (_b = el.error) === null || _b === void 0 ? void 0 : _b.message } : null,
        networkState: NETWORK_STATE[el.networkState],
        preload: el.preload,
        readyState: READY_STATE[el.readyState],
        seekable: timeRangesToObjectArray(el.seekable),
        seeking: el.seeking,
        src: dataUrlHandler(el.src || el.currentSrc),
        textTracks: textTrackListToJson(el.textTracks),
        fileExtension: getUriFileExtension(el.currentSrc),
        fullscreen: isFullScreen(el),
        pictureInPicture: document.pictureInPictureElement === el,
    };
    if (el.disableRemotePlayback)
        data.disableRemotePlayback = el.disableRemotePlayback;
    if (el.crossOrigin)
        data.crossOrigin = el.crossOrigin;
    return {
        schema: 'iglu:org.whatwg/media_element/jsonschema/1-0-0',
        data: data,
    };
}
function buildHTMLVideoElementEntity(el) {
    var data = {
        poster: el.poster,
        videoHeight: el.videoHeight,
        videoWidth: el.videoWidth,
    };
    if (el.hasAttribute('autopictureinpicture'))
        data.autoPictureInPicture = true;
    if (el.disablePictureInPicture)
        data.disablePictureInPicture = el.disablePictureInPicture;
    return {
        schema: 'iglu:org.whatwg/video_element/jsonschema/1-0-0',
        data: data,
    };
}

function updatePlayer(el) {
    var common = {
        currentTime: el.currentTime || 0,
        duration: getDuration(el),
        ended: el.ended,
        livestream: el.duration === Infinity,
        loop: el.loop,
        mediaType: isHtmlVideoElement(el) ? MediaType.Video : MediaType.Audio,
        muted: el.muted,
        paused: el.paused,
        volume: parseVolume(el.volume),
        playbackRate: el.playbackRate,
    };
    var video = el instanceof HTMLVideoElement
        ? {
            fullscreen: isFullScreen(el),
            pictureInPicture: document.pictureInPictureElement === el,
        }
        : {};
    return __assign(__assign({}, common), video);
}
function htmlContext(el) {
    var context = [function () { return buildHTMLMediaElementEntity(el); }];
    if (el instanceof HTMLVideoElement) {
        context.push(function () { return buildHTMLVideoElementEntity(el); });
    }
    return context;
}
function setUpListeners(config) {
    var _a;
    var id = config.id, video = config.video, label = config.label;
    startMediaTracking(__assign(__assign({}, config), { id: id, player: __assign({ label: label }, updatePlayer(video)), context: ((_a = config.context) !== null && _a !== void 0 ? _a : []).concat(htmlContext(video)) }));
    // If metadata is already loaded, we can track the ready event immediately
    if (video.readyState > 0) {
        trackMediaReady({ id: id, player: updatePlayer(video) });
    }
    else {
        video.addEventListener('loadedmetadata', function () {
            trackMediaReady({ id: id, player: updatePlayer(video) });
        });
    }
    addVideoEventListeners(video, id);
}
function addVideoEventListeners(video, id) {
    var isWaiting = false;
    video.addEventListener('play', function () { return trackMediaPlay({ id: id, player: updatePlayer(video) }); });
    video.addEventListener('pause', function () { return trackMediaPause({ id: id, player: updatePlayer(video) }); });
    video.addEventListener('ended', function () { return trackMediaEnd({ id: id, player: updatePlayer(video) }); });
    video.addEventListener('seeked', function () { return trackMediaSeekEnd({ id: id, player: updatePlayer(video) }); });
    video.addEventListener('ratechange', function () {
        return trackMediaPlaybackRateChange({ id: id, player: updatePlayer(video), newRate: video.playbackRate });
    });
    video.addEventListener('volumechange', function () {
        trackMediaVolumeChange({ id: id, player: updatePlayer(video), newVolume: parseVolume(video.volume) });
    });
    video.addEventListener('fullscreenchange', function () {
        trackMediaFullscreenChange({
            id: id,
            player: updatePlayer(video),
            fullscreen: document.fullscreenElement === video,
        });
    });
    video.addEventListener('enterpictureinpicture', function () {
        trackMediaPictureInPictureChange({
            id: id,
            player: updatePlayer(video),
            pictureInPicture: true,
        });
    });
    video.addEventListener('leavepictureinpicture', function () {
        trackMediaPictureInPictureChange({
            id: id,
            player: updatePlayer(video),
            pictureInPicture: false,
        });
    });
    video.addEventListener('waiting', function () {
        trackMediaBufferStart({ id: id, player: updatePlayer(video) });
        isWaiting = true;
    });
    // `playing` is also triggered by playing the video after pausing,
    // so we need to check if the video was waiting before tracking the buffer
    video.addEventListener('playing', function () {
        if (isWaiting) {
            trackMediaBufferEnd({ id: id, player: updatePlayer(video) });
            isWaiting = false;
        }
    });
    video.addEventListener('error', function () {
        trackMediaError({ id: id, player: updatePlayer(video) });
    });
    video.addEventListener('timeupdate', function () {
        updateMediaTracking({ id: id, player: updatePlayer(video) });
    });
}

var LOG;
function MediaTrackingPlugin() {
    return __assign(__assign({}, SnowplowMediaPlugin()), { logger: function (logger) {
            LOG = logger;
        } });
}
/**
 * Enables media tracking on an HTML `<video>` or `<audio>` element.
 *
 * @param {Config} config - Configuration options for media tracking.
 *
 * **Required:**
 * - `config.id` (`string`): The session ID. Must be unique for each media element. Used to identify the media session and end tracking.
 * - `config.video` (string | HTMLMediaElement): The ID of the media element or the element itself.
 *
 * **Optional:**
 * - `config.label` (`string`): A human-readable label for the media element.
 * - `config.captureEvents` {@link HTML5MediaEventTypes}: A list of media events to track. All events are tracked by default.
 * - `config.boundaries` (`number[]`): Percentage thresholds (0-100) at which to trigger progress events. Disabled by default.
 * - `config.context` {@link DynamicContext}: Contexts to attach to each tracking event.
 *
 * **Ping Configuration (Optional):**
 * - `config.pings.pingInterval` (`number`): The interval (in seconds) for sending ping events. Default is 30(s).
 * - `config.pings.maxPausedPings` (`number`): The maximum number of ping events sent while playback is paused. Default is 1.
 *
 * **Other Options (Optional):**
 * - `config.updatePageActivityWhilePlaying` (`boolean`): Whether to update page activity while media is playing. Enabled by default.
 * - `config.filterOutRepeatedEvents` {@link FilterOutRepeatedEvents}: Whether to suppress consecutive identical events. Default is false.
 */
function startHtml5MediaTracking(config) {
    if (!config.video) {
        LOG.error("Missing 'video' property in the enableMediaTracking configuration. ", "Ensure the 'config.video' property is correctly set. Current config: ", config);
        return;
    }
    config = setConfigDefaults(config);
    if (isStringConfig(config)) {
        waitForElement(config, setUpListeners);
    }
    else if (isElementConfig(config)) {
        setUpListeners(config);
    }
}
/**
 * Ends media tracking with the given ID if previously started. Clears local state for the media tracking and sends any events waiting to be sent.
 *
 * @param {string} id - The session ID.
 */
function endHtml5MediaTracking(id) {
    endMediaTracking({ id: id });
}

export { MediaTrackingPlugin, endHtml5MediaTracking, startHtml5MediaTracking };
//# sourceMappingURL=index.module.js.map
